import {createSelector} from '@reduxjs/toolkit'
import {adaptSapiPriceToChargeablePrice} from 'business/adaptSapiPriceToChargeablePrice'
import {decorateSapiResponseWithChargeablePrices} from 'business/decorateSapiResponseWithChargeablePrices'
import {getDisplayTotalPrice} from 'business/price'
import getNumberOfNights from 'components/data/URLParams/mappers/getNumberOfNights'
import getRoomConfig from 'components/data/URLParams/mappers/getRoomConfig'
import {comparator, sort, take} from 'ramda'
import {RootState} from 'store'
import {urlParams} from 'utils/urlParams'

import {SapiOfferWithChargeablePrices} from '@daedalus/core/src/offer/business/offersMapping'
import {OfferPrice} from '@daedalus/core/src/offer/types/offer'
import {
  GetOffersParams,
  OffersState,
  searchApi
} from '@daedalus/core/src/sapi/services/searchApi'

import {getTaxDisplayLogic} from '../rooms/selectors'

const BEST_OFFERS_COUNT = 2

const sortOfferByTotalPrice = (numberOfRooms: number) =>
  comparator(
    (a: SapiOfferWithChargeablePrices, b: SapiOfferWithChargeablePrices) => {
      return (
        getDisplayTotalPrice(a.prices, numberOfRooms) <
        getDisplayTotalPrice(b.prices, numberOfRooms)
      )
    }
  )

export const getSapiOffersData = (state: RootState) =>
  searchApi.endpoints.getOffers.select({
    useSingleCacheKey: true
  } as unknown as GetOffersParams)(state)

export const getSapiAnchorPriceRateBreakDown = createSelector(
  getSapiOffersData,
  sapiData => sapiData.data?.offerEntity?.anchorPriceRateBreakdown
)

export const getSapiOffers = createSelector(
  getSapiOffersData,
  sapiData => sapiData.data?.offerEntity?.offers || []
)

export const getAdaptedSapiOffers = createSelector(
  getSapiOffers,
  getTaxDisplayLogic,
  (offers, taxDisplayLogic) => {
    const numberOfNights = getNumberOfNights(urlParams())
    return decorateSapiResponseWithChargeablePrices(
      offers,
      taxDisplayLogic,
      numberOfNights
    )
  }
)

export const getSapiAnchorPriceBreakdown = createSelector(
  getTaxDisplayLogic,
  getSapiAnchorPriceRateBreakDown,
  (taxDisplayLogic, anchorPriceRateBreakdown) => {
    const params = urlParams()
    const numberOfNights = getNumberOfNights(params)

    if (!anchorPriceRateBreakdown) return null

    return adaptSapiPriceToChargeablePrice(
      anchorPriceRateBreakdown,
      taxDisplayLogic,
      params.currency,
      numberOfNights,
      1
    )
  }
)

export const getSapiBestOffers = createSelector(
  getAdaptedSapiOffers,
  offers => {
    const {numberOfRooms} = getRoomConfig(urlParams())

    return sort(
      sortOfferByTotalPrice(numberOfRooms),
      take(BEST_OFFERS_COUNT, offers)
    )
  }
)

export const extractSapiAnchorPriceNightlyTotal = (
  anchorPriceBreakDown?: OfferPrice | null
) => anchorPriceBreakDown?.chargeable?.nightlyDisplayTotal ?? 0

export const getSapiAnchorPriceNightlyTotal = createSelector(
  getSapiAnchorPriceBreakdown,
  anchorPriceBreakDown =>
    extractSapiAnchorPriceNightlyTotal(anchorPriceBreakDown)
)

export const getSapiLowestTotalPrice = createSelector(
  getSapiBestOffers,
  sortedOffers => {
    const {numberOfRooms} = getRoomConfig(urlParams())

    if (sortedOffers.length === 0) {
      return null
    }

    return getDisplayTotalPrice(sortedOffers[0].prices, numberOfRooms)
  }
)

export const getIsSapiOffersDone = createSelector(getSapiOffersData, sapiData =>
  Boolean(sapiData.data?.status === OffersState.Completed)
)
